import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAllTemplates, deleteTemplate } from '../../api/invoiceTemplates'
import type { InvoiceTemplate } from '../../api/invoiceTemplates'
import { useCustomersByRole } from '../../hooks/useCustomersByRole'

export default function InvoiceTemplates() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<InvoiceTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const { customers } = useCustomersByRole('Cus')

  useEffect(() => {
    loadTemplates()
  }, [])

  async function loadTemplates() {
    try {
      const data = await getAllTemplates()
      setTemplates(data)
    } catch (err) {
      console.error('Error loading templates:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleDelete(id: string) {
    if (!confirm('Delete this template? Future uploads from this customer will go through AI again.')) return
    try {
      await deleteTemplate(id)
      setTemplates(templates.filter(t => t.id !== id))
    } catch (err) {
      console.error('Error deleting template:', err)
    }
  }

  function customerLabel(template: InvoiceTemplate): string {
    if (template.is_orphaned || !template.customer_id) return 'Orphaned'
    const c = customers.find(c => c.id === template.customer_id)
    return c ? `${c.code} – ${c.name}` : template.customer_id
  }

  function formatLastUsed(template: InvoiceTemplate): string {
    if (!template.last_used_at) return 'Never'
    return new Date(template.last_used_at).toLocaleDateString()
  }

  function structureTypeLabel(template: InvoiceTemplate): string {
    if (template.structure_type === 'single_sheet') return 'Single sheet'
    if (template.structure_type === 'multi_sheet') return 'Multi-sheet'
    if (template.structure_type === 'two_files') return 'Two files'
    return '—'
  }

  const groups = (() => {
    const map = new Map<string, { label: string; templates: InvoiceTemplate[] }>()
    for (const t of templates) {
      const key = t.is_orphaned || !t.customer_id ? 'orphaned' : t.customer_id
      if (!map.has(key)) map.set(key, { label: customerLabel(t), templates: [] })
      map.get(key)!.templates.push(t)
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.label === 'Orphaned') return 1
      if (b.label === 'Orphaned') return -1
      return a.label.localeCompare(b.label)
    })
  })()

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/customs/invoices')} className="text-gray-500 hover:text-gray-700">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Invoice Templates</h1>
          </div>
          <button
            onClick={() => navigate('/customs/invoices/templates/new')}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
          >
            + Create Template
          </button>
        </div>

        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          A customer can have more than one template (different suppliers, redesigns, product lines) --
          uploads are auto-matched to the right one by layout. Templates also get created automatically the
          first time an invoice is uploaded and saved for a customer with none yet — use{' '}
          <button onClick={() => navigate('/customs/invoices/templates/new')} className="text-blue-600 dark:text-blue-400 hover:underline">Create Template</button>{' '}
          for the guided, per-field setup instead.
        </p>

        {/* Templates List */}
        {loading ? (
          <div className="text-center py-12">Loading templates...</div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <p>No templates yet. Upload and save an invoice for a customer to create one.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.map(group => (
              <div key={group.label} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-3 bg-gray-100 dark:bg-gray-700/70 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-800 dark:text-gray-200">
                    {group.label === 'Orphaned' ? (
                      <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 text-xs rounded">Orphaned</span>
                    ) : group.label}
                  </h2>
                  <span className="text-xs text-gray-500 dark:text-gray-400">{group.templates.length} template{group.templates.length !== 1 ? 's' : ''}</span>
                </div>
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Name</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">File Type</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Structure</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Mapping</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Uses</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Last Used</th>
                      <th className="px-6 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {group.templates.map(template => (
                      <Fragment key={template.id}>
                        <tr className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">
                            {template.name || '—'}{template.agent_name && !template.name ? ` (was: ${template.agent_name})` : ''}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{template.file_type}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{structureTypeLabel(template)}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                            <button
                              onClick={() => setExpandedId(expandedId === template.id ? null : template.id!)}
                              className="text-blue-600 dark:text-blue-400 hover:underline"
                            >
                              {expandedId === template.id ? 'Hide' : 'View'}
                            </button>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{template.usage_count || 0}</td>
                          <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{formatLastUsed(template)}</td>
                          <td className="px-6 py-4 text-sm">
                            <div className="flex gap-2">
                              <button onClick={() => navigate(`/customs/invoices/templates/${template.id}/edit`)} className="px-3 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded">
                                Edit
                              </button>
                              <button onClick={() => handleDelete(template.id!)} className="px-3 py-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded">
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedId === template.id && (
                          <tr>
                            <td colSpan={7} className="px-6 py-4 bg-gray-50 dark:bg-gray-900/30">
                              {(template.signature?.shipperText || template.signature?.fileNamePattern) && (
                                <>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">signature (checked first, before layout matching)</p>
                                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all max-h-32 overflow-y-auto mb-3">
                                    {JSON.stringify(template.signature, null, 2)}
                                  </pre>
                                </>
                              )}
                              {template.structure_type === 'multi_sheet' && template.sheet_assignments && (
                                <>
                                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">sheet_assignments</p>
                                  <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all max-h-32 overflow-y-auto mb-3">
                                    {JSON.stringify(template.sheet_assignments, null, 2)}
                                  </pre>
                                </>
                              )}
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">field_mappings</p>
                              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all max-h-64 overflow-y-auto mb-3">
                                {JSON.stringify(template.field_mappings, null, 2)}
                              </pre>
                              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">structure_signature (auto-match fingerprint)</p>
                              <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap break-all max-h-64 overflow-y-auto">
                                {JSON.stringify(template.structure_signature, null, 2)}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
